import axios from 'axios';

import type { DispatchSerialCheck, SerialValidationRecord } from '@/domain/izzi';
import { VITE_URL_SERVICE } from '@/utils/envs';

const RELEASED_HOURS = 168;

const httpPublic = axios.create({
  baseURL: VITE_URL_SERVICE,
  headers: {
    Accept: 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

const toSerialCheck = (
  iccid: string,
  outcome: DispatchSerialCheck['outcome'],
  row?: SerialValidationRecord,
): DispatchSerialCheck => ({
  iccid,
  outcome,
  purchaseOrder: row?.purchaseOrder,
  customerSO: row?.customerSO,
  sku: row?.sku,
  izziMessage: row?.izziMessage,
  quarantinedAt: row?.quarantinedAt,
  releasedAt: row?.releasedAt,
});

export const isSerialBlocked = (check: DispatchSerialCheck): boolean =>
  check.outcome === 'QUARANTINED' || check.outcome === 'REJECTED' || check.outcome === 'ERROR';

export const checkSerialStatus = async (iccid: string): Promise<DispatchSerialCheck> => {
  const [quarantined, released] = await Promise.all([
    httpPublic.get<{ data: SerialValidationRecord[] }>('/api/v3/ops-izzi/serials', {
      params: { status: 'QUARANTINED', iccid, page: 1, pageSize: 1 },
    }),
    httpPublic.get<{ data: SerialValidationRecord[] }>('/api/v3/ops-izzi/serials', {
      params: { status: 'RELEASED', iccid, hours: RELEASED_HOURS, page: 1, pageSize: 1 },
    }),
  ]);

  if (quarantined.data.data[0]) {
    return toSerialCheck(iccid, 'QUARANTINED', quarantined.data.data[0]);
  }

  if (released.data.data[0]) {
    return toSerialCheck(iccid, 'RELEASED', released.data.data[0]);
  }

  try {
    const { data } = await httpPublic.get<{ data: SerialValidationRecord[] }>(
      '/api/v3/ops-izzi/serial-validations/errors',
      { params: { page: 0, pageSize: 1, iccid } },
    );
    const row = data.data[0];
    if (row) {
      return toSerialCheck(iccid, row.status === 'REJECTED' ? 'REJECTED' : 'ERROR', row);
    }
  } catch {
    // Sin PO no hay historial de validación; cuarentena y liberadas ya se checaron
  }

  return toSerialCheck(iccid, 'CLEAR');
};
